import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { ActiveCartFacade, OrderEntry } from '@spartacus/cart/base/root'
import { UserIdService } from '@spartacus/core'
import { Observable, Subject, combineLatest, of, throwError } from 'rxjs'
import { catchError, debounceTime, map, mergeMap, retry } from 'rxjs/operators'
import { SharedKey, SharedValue } from '../models/enum/shared.enum'
import { ICustomProduct } from '../models/interface/product.interface'
import { ISharedPagination } from '../models/interface/shared.interface'
import { AppConfigurationService } from './app-configuration.service'
import { SharedService } from './shared/shared.service'

@Injectable({
  providedIn: 'root'
})
export class CustomCartService {
  private validateQuantitySource = new Subject()
  public validateQuantity$ = this.validateQuantitySource.asObservable()

  constructor(
    protected userIdService: UserIdService,
    protected activeCartService: ActiveCartFacade,
    private appConfigurationService: AppConfigurationService,
    private http: HttpClient,
    private sharedService: SharedService
  ) {}

  public UomCode: string

  setUomCode(UomCode: string): void {
    this.UomCode = UomCode
  }

  getUomCode(): string {
    return this.UomCode
  }

  public validateItemQuantity(): void {
    this.validateQuantitySource.next(true)
  }

  public uploadCSVCart(file: any): Observable<any> {
    const newFile: any = new File([file], file.name, { type: 'text/csv' })
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/cart/import/csv?fields=DEFAULT`

    const formData = new FormData()
    formData.append('file', newFile)
    return this.http.post<any>(url, formData).pipe(
      retry(1),
      catchError(() => {
        return throwError({ message: SharedValue.errorTryAgain })
      })
    )
  }

  public getSavedCarts(params?: ISharedPagination): Observable<any> {
    let payload: HttpParams = new HttpParams()
      .set(SharedKey.pageSize, SharedValue.pageSize.toString())
      .set('fields', 'DEFAULT')

    if (params?.pageNumber) {
      payload = payload.set(SharedKey.currentPage, params.pageNumber.toString())
    }
    if (params?.sort) {
      payload = payload.set(SharedKey.sort, params.sort)
    }

    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/saved-carts`
    return this.http.get<any>(url, { params: payload }).pipe(
      retry(1),
      catchError(() => {
        throw new Error(SharedValue.errorTryAgain)
      })
    )
  }

  public exportCart(cartId: string): Observable<any> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/cart/export/${cartId}`
    return this.http
      .get(url, {
        responseType: 'text'
      })
      .pipe(
        retry(1),
        catchError(() => {
          throw new Error(SharedValue.errorTryAgain)
        })
      )
  }

  public restoreCart(cartId: string): Observable<any> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/carts/${cartId}/restoresavedcart`
    return this.http.patch(url, null).pipe(
      retry(1),
      catchError(() => {
        throw new Error(SharedValue.errorTryAgain)
      })
    )
  }

  public getCurrentCart(): Observable<any> {
    const params: HttpParams = new HttpParams().set('fields', 'FULL')

    const url = `${this.appConfigurationService.getBaseSiteUrl()}/users/current/carts/current`
    return this.http.get<any>(url, { params }).pipe(retry(1), catchError(this.handleError))
  }

  public getCart(): Observable<any> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    return combineLatest([this.userIdService.getUserId(), this.activeCartService.getActiveCartId()]).pipe(
      debounceTime(0),
      mergeMap(([userId, cartId]) =>
        this.http.get<any>(`${baseUrl}/users/${userId}/carts/${cartId}`).pipe(retry(1), catchError(this.handleError))
      )
    )
  }

  public AddSpecialInstructions(
    cartId: string,
    specialInstructions?: string,
    requestedDeliveryDate?: string
  ): Observable<any> {
    const params: HttpParams = new HttpParams()
      .set('specialInstructions', specialInstructions)
      .set('requestedDeliveryDate', requestedDeliveryDate)
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/${cartId}/updateDeliveryMode`
    return this.http.post(url, null, { params }).pipe(retry(1), catchError(this.sharedService.handleError))
  }

  public simulateCart(cartId: string): Observable<any> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/carts/${cartId}/simulation`
    return this.http.get<any>(url).pipe(
      retry(1),
      catchError(() => {
        throw new Error(SharedValue.errorTryAgain)
      })
    )
  }

  private handleError(err: HttpErrorResponse): any {
    let errorMsg: string
    if (err.error instanceof ErrorEvent) {
      errorMsg = 'An error occurred: ' + err.error.message
    } else {
      if (err.error?.errors?.length) {
        errorMsg = err.error.errors[0].message
      } else {
        errorMsg = err.message
      }
    }
    return throwError(errorMsg)
  }

  public savecart(cartId: string): Observable<any> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/carts/${cartId}/save`
    return this.http.post(url, null).pipe(
      retry(1),
      catchError(() => {
        throw new Error(SharedValue.errorTryAgain)
      })
    )
  }

  public getConstCenterAddresses(cartId: string): Observable<any> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/carts/${cartId}/costcenter?fields=DEFAULT`
    return this.http.put<any>(url, null).pipe(
      map((res) => {
        return res?.costCenter?.unit?.addresses.filter((item) => item.shippingAddress)
      }),
      catchError(() => {
        return of([])
      })
    )
  }

  public downloadCsvTemplate(): Observable<any> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/cartDownload/csv`
    return this.http.get(url, {
      observe: 'response',
      responseType: 'blob'
    })
  }

  public validateCsvTemplate(file: File): Observable<any> {
    const formData = new FormData()
    formData.set('file', new Blob([file], { type: 'text/csv' }), file.name)

    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/cart/csv/validate`
    return this.http.post(url, formData).pipe(
      catchError((err) => {
        if (err.error?.errors?.length) {
          let errorMsg: string
          errorMsg = err.error.errors[0].message
          errorMsg = errorMsg.substring(1, errorMsg.length - 1)
          errorMsg = errorMsg.replace(/,/g, '\n')
          return throwError({ message: errorMsg })
        }
        return throwError(SharedValue.errorTryAgain)
      })
    )
  }

  public sortOutOfStockItemsFirst(items: OrderEntry[]): OrderEntry[] {
    return items
      ?.map((item) => {
        if (!this.setHasStock(item.product)) {
          return { order: 1, item }
        }
        return { order: 0, item }
      })
      .sort((a, b) => {
        return b.order - a.order
      })
      .map((customItem) => customItem.item)
  }

  public setHasStock(product: ICustomProduct): boolean {
    return product?.stock?.stockLevelStatus !== 'outOfStock'
  }

  public downloadTermsAndConditions(): Observable<any> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/getPurchaseTermsUrl`

    return this.http.get(url, { responseType: 'text' }).pipe(
      map((res: string) => {
        return this.sharedService.getMediaUrl(res)
      })
    )
  }
}
